import type { ICompany, IDocument, IEstate, ISalesPackage, ISalesPackageOptions, ISellingRegion, SalesPackageEvents, SalesPackageState } from '@condo/domain';
import { getClient } from './api-client';

export interface IFetchSalesPackagesQueryOpts {
    term?: string;
    state?: SalesPackageState[];
    withRelations: boolean;
}

export interface ICreateSalesPackageParams {
    estateIds: number[];
    salesPackage: {
        title?: string;
        distributionPartnerId?: number;
        secondaryDistributionPartners?: ICompany[];
        isSalesPackageOpen?: boolean;
        firstLoanInterestRate: number;
        firstLoanAmortizationRate: number;
        secondLoanInterestRate: number;
        secondLoanAmortizationRate: number;
        specialInfo?: string;
    };
}

export interface IUpdateSalesPackageParams {
    title?: string;
    distributionPartnerId?: number;
    secondaryDistributionPartners?: ICompany[];
    isSalesPackageOpen?: boolean;
    firstLoanInterestRate: number;
    firstLoanAmortizationRate: number;
    secondLoanInterestRate: number;
    secondLoanAmortizationRate: number;
    visibleInSalesPortal?: boolean;
    isArchived?: boolean;
    recalculateInvestorCashflows?: boolean;
    specialInfo?: string;
}

export const fetchSalesPackages = async (queryOpts?: ISalesPackageOptions): Promise<{ salesPackages: ISalesPackage[]; total: number }> =>
    getClient('basecamp')
        .get('/sales-packages', { params: queryOpts })
        .then(({ data }) => data);

export const fetchSalesPackage = async (salesPackageId: number, params): Promise<ISalesPackage> =>
    getClient('basecamp')
        .get(`/sales-packages/${salesPackageId}`, { params })
        .then(({ data }) => data.salesPackage);

export const fetchSalesPackageEligibleEstates = async (query: Record<string, string | number>): Promise<{ estates: IEstate[]; total: number }> =>
    getClient('basecamp')
        .get('/sales-packages/eligible-estates', { params: query })
        .then(({ data }) => data);

export const addEstatesToSalesPackage = async (
    salesPackageId: number,
    estates: { estateId: number; salesPackageUnitNumber: number }[],
): Promise<ISalesPackage> =>
    getClient('basecamp')
        .post(`/sales-packages/${salesPackageId}/estates`, { estates })
        .then(({ data }) => data.salesPackage);

export const updateSalesPackageEstates = async (
    salesPackageId: number,
    { estates, withEstateRelations }: { estates: { estateId: number; salesPackageUnitNumber: number }[]; withEstateRelations?: boolean },
): Promise<ISalesPackage> =>
    getClient('basecamp')
        .put(`/sales-packages/${salesPackageId}/estates`, { estates, withEstateRelations })
        .then(({ data }) => data.salesPackage);

export const createSalesPackage = async (salesPackageDto: ICreateSalesPackageParams): Promise<ISalesPackage> =>
    getClient('basecamp')
        .post('/sales-packages', salesPackageDto)
        .then(({ data }) => data.salesPackage);

export const updateSalesPackage = async (salesPackageId: number, salesPackageDto: IUpdateSalesPackageParams): Promise<ISalesPackage> =>
    getClient('basecamp')
        .put(`/sales-packages/${salesPackageId}`, salesPackageDto)
        .then(({ data }) => data.salesPackage);

export const triggerSalesPackageEvent = async (salesPackageId: number, event: SalesPackageEvents): Promise<ISalesPackage> =>
    getClient('basecamp')
        .post(`/sales-packages/${salesPackageId}/event`, { event })
        .then(({ data }) => data.salesPackage);

export const getSalesPackageTitles = async (): Promise<{ title?: string; salesPackageId?: number }[]> =>
    getClient('basecamp')
        .get('/sales-packages/titles')
        .then(({ data }) => data);

export const getSalesPackageDocuments = async (salesPackageId: number): Promise<{ salesPackageDocuments: IDocument[]; sellingRegions?: ISellingRegion[] }> =>
    getClient('basecamp')
        .get(`/sales-packages/${salesPackageId}/documents`)
        .then(({ data }) => data);
